<template>
  <div>
    <div class="align-items-center justify-content-center d-md-flex m-5">
      <b-card style="border-radius: 20px; border: 1px solid lightgrey" class="px-1 m-0">

        <div class="mt-1 text-center">
          <b-link :to="{ name: 'Login' }">
            <img :src="appLogoImage" img-fluid width="200" />
          </b-link>
        </div>

        <b-card-title title-tag="h2" class="font-weight-bolder mb-50 mt-2 text-center">
          Welcome to Signal Sure Agent AI
        </b-card-title>

        <validation-observer ref="loginFormValidation">
          <b-form class="auth-login-form mt-2" @submit.prevent>

            <!-- Full Name -->
            <b-form-group label="Full Name">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="fullname" :state="errors.length ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Username -->
            <b-form-group label="Username">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="username" :state="errors.length ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Email -->
            <b-form-group label="Business Email">
              <validation-provider rules="required|email" v-slot="{ errors }">
                <b-form-input v-model="email" :state="errors.length ? false : null" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Password -->
            <b-form-group label="Password">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-input-group>
                  <b-form-input
                    v-model="password"
                    :type="passwordFieldType"
                    :state="errors.length ? false : null"
                  />
                  <b-input-group-append is-text>
                    <feather-icon
                      class="cursor-pointer"
                      :icon="passwordToggleIcon"
                      @click="togglePasswordVisibility"
                    />
                  </b-input-group-append>
                </b-input-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- License Type -->
            <b-form-group label="License Type">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-radio-group v-model="license_type">
                  <b-form-radio value="individual">Individual Producer</b-form-radio>
                  <b-form-radio value="agency">Agency</b-form-radio>
                </b-form-radio-group>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Agency Name -->
            <b-form-group v-if="license_type === 'agency'" label="Agency Name">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="agency_name" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Business Phone -->
            <b-form-group label="Business Phone">
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-input v-model="business_phone" />
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <!-- Compliance -->
            <b-form-group>
              <validation-provider rules="required" v-slot="{ errors }">
                <b-form-checkbox v-model="compliance_attested">
                  I confirm all compliance & licensing information is correct
                </b-form-checkbox>
                <small class="text-danger">{{ errors[0] }}</small>
              </validation-provider>
            </b-form-group>

            <b-button block variant="primary" class="mt-3" @click="validateForm" :disabled="isSubmitting">
              <b-spinner v-if="isSubmitting" small />
              <span v-else>Sign Up</span>
            </b-button>

          </b-form>
        </validation-observer>
      </b-card>
    </div>
  </div>
</template>

<script>
import { togglePasswordVisibility } from "@core/mixins/ui/forms";
import { $themeConfig } from "@themeConfig";
import { mapActions } from "vuex";
import ToastificationContent from "@core/components/toastification/ToastificationContent.vue";

export default {
  mixins: [togglePasswordVisibility],

  data() {
    return {
      appLogoImage: "",
      password: "",
      fullname: "",
      email: "",
      username: "",
      agency_name: "",
      business_phone: "",
      license_type: "",
      services_enabled: [],
      compliance_attested: false,
      isSubmitting: false,
    };
  },

  created() {
    this.appLogoImage = $themeConfig.app.appLogoImage;
  },

  methods: {
    ...mapActions({
      createSignup: "appData/createSignup",
    }),

    async validateForm() {
      const valid = await this.$refs.loginFormValidation.validate();
      if (valid) this.submit();
    },

    async submit() {
      this.isSubmitting = true;

      const payload = {
        user: {
          username: this.username,
          password: this.password,
          is_agent: true,
        },
        agent_profile: {
          full_name: this.fullname,
          agency_name: this.license_type === "agency" ? this.agency_name : null,
          business_email: this.email,
          business_phone: this.business_phone,
          license_type: this.license_type,
          services_enabled: this.services_enabled,
          compliance_attested: this.compliance_attested,
        },
      };

      try {
        const res = await this.createSignup(payload);

        if (res.status === 201) {
          this.$toast(
            {
              component: ToastificationContent,
              props: {
                title: "User Signup Successfully",
                variant: "success",
                icon: "BellIcon",
              },
            },
            { position: "top-right" }
          );
          this.$router.push({ name: "SignupConfirmation" });
        }
      } catch (error) {
        console.error("Signup error:", error);
      } finally {
        this.isSubmitting = false;
      }
    },
  },

  computed: {
    passwordToggleIcon() {
      return this.passwordFieldType === "password" ? "EyeIcon" : "EyeOffIcon";
    },
  },
};
</script>

<style lang="scss">
@import "@core/scss/vue/pages/page-auth.scss";
</style>
